import React, { useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { mapLocation } from 'lib/carDetailsLib'

import PageTitle from 'components/atoms/PageTitle'
import SearchField from 'components/atoms/SearchField'
import ConfirmBar from 'components/atoms/ConfirmBar'
import CarFiltersColumn from 'components/atoms/CarsFiltersColumn'
import CarCard from 'components/atoms/CarCard'
import CarsList from 'components/atoms/CarsList'
import PagePartnersSelect from 'components/atoms/PagePartnersSelect'
import DownloadBar from 'components/atoms/DownloadBar'

import useCarsFilters from 'hooks/useCarsFilters'
import useInputOutput from 'hooks/useInputOutput'

import { getRepriceValues } from '../../../lib/repriceFee'

import CarCardChoices from './CarCardChoices'
import ChoicesRecap from './ChoicesRecap'
import CarsToBuyPageTitleRightColumn from './CarsToBuyPageTitleRightColumn'
import CarsToBuySessionExpired from './CarsToBuySessionExpired'
import { getCarsToBuyUrl } from './carsToBuyLib'
import CarsToBuyDetails from './CarsToBuyDetails'

const mapCars = ({
  admin,
  filteredCars,
  partnerId,
  readOnly,
  session,
  updateChoice,
  setShowDetails,
  contentIsReadOnly,
}) =>
  filteredCars.map((car, i) => {
    const repriceValues = car.reprice
      ? getRepriceValues(car.reprice, car.feePercentage)
      : {}

    const price = car.reprice ? repriceValues.repriceNet : car.price
    const fee = car.reprice ? repriceValues.repriceFee : car.fee

    return (
      <CarCard
        data-testid={`pubblicazione-car-${i}`}
        key={car.originalId}
        onClick={() => setShowDetails(car.vin)}
        height={153 + 20}
        car={car}
        price={price}
        fee={fee}
        maxPrice={car.maxPrice}
        right={
          <CarCardChoices
            session={session}
            partnerId={partnerId}
            car={car}
            readOnly={admin || readOnly || contentIsReadOnly}
            updateChoice={updateChoice}
          />
        }
      />
    )
  })

function getRowsForDownload(titlesRow, cars, t) {
  return [
    titlesRow,
    ...cars.map((car) => [
      `${car.title}`,
      car.km,
      mapLocation(car.location),
      t(`carDetails.mapping.${car.transmission}`),
      car.reconditioningEstCost || '-',
      car.year,
      car.fuelType,
      car.plate,
      // car.vin,
      Math.round(car.optionAmount) || '-',
      car.choice === null ? '-' : t(`carsToBuy.choice.${car.choice}`),
    ]),
  ]
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function CarsToBuy({
  session,
  previousSession,
  admin,
  partnerId,
  partners,
  updateChoice = () => {},
  addMessage,
  cars,
  countDown,
  readOnly,
  contentIsReadOnly = false,
}) {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const [showDetails, setShowDetails] = useState()
  const [filteredCars, setFilteredCars] = useState([])

  const { saveXls } = useInputOutput()

  const {
    filters,
    filtersData,
    initFilters,
    setKmRange,
    setYearRange,
    setBrands,
    setModels,
    setFuels,
    setQuery,
    setChoice: setFilteringChoice,
    filterCars,
    resetFilters,
  } = useCarsFilters()

  useEffect(() => {
    if (!cars.length || filters?.initialized) {
      return
    }

    initFilters(cars)
  }, [cars, filters?.initialized, initFilters])

  const handleSetFilteredCars = useDebouncedCallback(
    (cars, filters, setFilteredCars, filterCars) =>
      setFilteredCars(filterCars(cars, filters)),
    100
  )

  useEffect(() => {
    handleSetFilteredCars(cars, filters, setFilteredCars, filterCars)
  }, [cars, handleSetFilteredCars, filters, setFilteredCars, filterCars])

  const handleChangeSearch = (event) => {
    setQuery(event.target.value)
  }

  const carsContent = useMemo(
    () =>
      mapCars({
        admin,
        filteredCars,
        partnerId,
        readOnly,
        session,
        updateChoice,
        setShowDetails,
        contentIsReadOnly,
      }),
    [
      admin,
      filteredCars,
      partnerId,
      readOnly,
      session,
      updateChoice,
      contentIsReadOnly,
    ]
  )

  const carFilters = useMemo(
    () => (
      <CarFiltersColumn
        filters={filters}
        filtersData={filtersData}
        filteredCars={
          filteredCars?.length !== cars.length ? filteredCars : undefined
        }
        setKmRange={setKmRange}
        setYearRange={setYearRange}
        setBrands={setBrands}
        setModels={setModels}
        setFuels={setFuels}
        resetFilters={resetFilters}
        readOnly={readOnly}
      />
    ),
    [
      filters,
      filtersData,
      filteredCars,
      cars.length,
      setKmRange,
      setYearRange,
      setBrands,
      setModels,
      setFuels,
      resetFilters,
      readOnly,
    ]
  )

  const handlePartnerChange = (event) => {
    navigate(getCarsToBuyUrl(event.target.value))
  }

  const handleDownload = async () => {
    const titlesRow = [
      t('common.excel.name'),
      t('carDetails.km'),
      t('carDetails.location'),
      t('carDetails.transmission'),
      t('carDetails.damage'),
      t('carDetails.year'),
      t('carDetails.fuelType'),
      t('carDetails.plate'),
      // t('carDetails.vin'),
      t('carDetails.optionals'),
      t('common.excel.choice'),
    ]

    const rows = getRowsForDownload(titlesRow, cars, t)

    saveXls(`cars-to-buy`, rows)
  }

  return (
    <>
      {showDetails ? (
        <CarsToBuyDetails
          vin={showDetails}
          cars={filteredCars}
          allCars={cars}
          session={session}
          setShowDetails={setShowDetails}
          updateChoice={updateChoice}
        />
      ) : (
        <>
          <PageTitle
            label={t('carsToBuy.title')}
            rightColumn={
              <>
                <CarsToBuyPageTitleRightColumn countDown={countDown} />
                {partners.length > 0 && (
                  <>
                    <Box pr={2} />
                    <PagePartnersSelect
                      partners={partners}
                      value={partnerId}
                      handleChange={handlePartnerChange}
                      readOnly={readOnly}
                    />
                  </>
                )}
              </>
            }
          />
          {!session?.sessionId ||
          (countDown.initialized && countDown.expired) ? (
            <CarsToBuySessionExpired
              session={session}
              previousSession={previousSession}
            />
          ) : (
            <>
              <ConfirmBar
                label={t('carsToBuy.confirmButton')}
                left={
                  <SearchField
                    placeholder={t('common.form.searchBy')}
                    value={filters.query}
                    onChange={handleChangeSearch}
                    data-testid="text-search"
                  />
                }
                leftProps={{ sx: { width: 0.5 } }}
                right={<DownloadBar handleDownload={handleDownload} />}
                top={
                  <ChoicesRecap
                    cars={cars}
                    filteringChoice={filters.choice}
                    setFilteringChoice={setFilteringChoice}
                  />
                }
                topProps={{
                  sx: {
                    borderBottom: (theme) =>
                      `1px solid ${theme.palette.divider}`,
                  },
                }}
                hideConfirm
                sticky
              />
              <CarsList left={carFilters} right={carsContent} />
            </>
          )}
        </>
      )}
    </>
  )
}

CarsToBuy.propTypes = {
  session: T.object.isRequired,
  previousSession: T.object,
  admin: T.bool,
  partnerId: T.string,
  partners: T.array,
  updateChoice: T.func,
  addMessage: T.func,
  cars: T.array,
  countDown: T.object,
  readOnly: T.bool,
  contentIsReadOnly: T.bool,
}
